import { useState, useMemo } from "react";
import { Box, Typography, IconButton, TextField, Button, CircularProgress, InputAdornment } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SearchIcon from "@mui/icons-material/Search";
import { useGetAvailableOffersForAssignment, useAssignOffer } from '../../Query/OffersQuery';
import { useSnackbar } from '../../Stateless/SnackBarProvider';
import { useDebounce } from '../Utils/useDounce';

interface AssignOffersModalContentProps {
  customer: any;
  onClose: () => void;
  onAddOffer: () => void;
}

const AssignOffersModalContent = ({ customer, onClose, onAddOffer }: AssignOffersModalContentProps) => {
  const [quantities, setQuantities] = useState<{ [key: string]: number }>({});
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 300);
  const { data: availableOffers, isLoading: offersLoading } = useGetAvailableOffersForAssignment();
  const { mutate: assignOffer, isPending: assignLoading } = useAssignOffer();
  const { showMessage } = useSnackbar();

  const handleChange = (offerId: string, delta: number, offer: any) => {
    const currentQuantity = quantities[offerId] || 0;
    const newQuantity = currentQuantity + delta;
    
    // Validation: Check against couponPerUserLimit
    const couponPerUserLimit = offer.couponPerUserLimit;
    if (delta > 0 && couponPerUserLimit !== undefined && couponPerUserLimit !== null) {
      if (newQuantity > couponPerUserLimit) {
        showMessage(`Maximum ${couponPerUserLimit} coupons per user allowed for this offer`, 'error');
        return;
      }
    }
    
    // Validation: Check against totalCouponCount (available quantity)
    const totalCouponCount = offer.totalCouponCount;
    if (delta > 0 && totalCouponCount !== undefined && totalCouponCount !== null) {
      if (newQuantity > totalCouponCount) {
        showMessage(`Only ${totalCouponCount} coupons available for this offer`, 'error');
        return;
      }
    }
    
    setQuantities((prev) => ({
      ...prev,
      [offerId]: Math.max(0, newQuantity),
    }));
  };

  const handleSave = () => {
    if (!customer?.id && !customer?.userId) {
      showMessage('Customer information is missing', 'error');
      return;
    }

    // Filter offers with quantity > 0
    const offersToAssign = Object.entries(quantities)
      .filter(([_, quantity]) => quantity > 0)
      .map(([offerId, quantity]) => ({
        offerId,
        quantity,
      }));

    if (offersToAssign.length === 0) {
      showMessage('Please select at least one offer to assign', 'error');
      return;
    }

    const assignPayload = {
      userId: customer.id || customer.userId,
      offers: offersToAssign,
    };

    assignOffer(assignPayload, {
      onSuccess: () => {
        setQuantities({});
        onClose();
      },
    });
  };

  const allOffers = availableOffers?.data || [];

  // Filter offers based on search term
  const offers = useMemo(() => {
    if (!debouncedSearch.trim()) return allOffers;
    
    const searchLower = debouncedSearch.toLowerCase();
    return allOffers.filter((offer: any) => {
      const couponName = (offer.couponName || offer.title || '').toLowerCase();
      const couponCode = (offer.couponCode || '').toLowerCase();
      return couponName.includes(searchLower) || couponCode.includes(searchLower);
    });
  }, [allOffers, debouncedSearch]);

  // Calculate expiration date
  const getExpirationDate = (startDate: string, validityDays: number) => {
    if (!startDate || !validityDays) return 'N/A';
    const date = new Date(startDate);
    date.setDate(date.getDate() + validityDays);
    return date.toLocaleDateString();
  };

  // Format offer value
  const formatOfferValue = (offer: any) => {
    if (offer.couponType === 'Percentage') {
      return `${offer.couponValue}%`;
    }
    return `${offer.couponValue} JOD`;
  };

  if (offersLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" mb={2}>
        Assign offers to: <b>{customer?.userName || customer?.customerName || 'Customer'}</b>
      </Typography>
      
      {/* Search Field */}
      <TextField
        fullWidth
        placeholder="Search offers by name or code"
        size="small"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 20, color: '#aaa' }} />
            </InputAdornment>
          ),
        }}
      />
      
      {offers.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
          {debouncedSearch.trim() ? 'No offers found matching your search' : 'No offers available for assignment'}
        </Typography>
      ) : (
        offers.map((offer: any) => {
          const offerId = offer.offerId || offer.id;
          const quantity = quantities[offerId] || 0;
          
          return (
            <Box
              key={offerId}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                p: 2,
                mb: 2,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 2,
                background: "#fafafa",
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight="bold">{offer.couponName || offer.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {offer.termsAndConditions || offer.description || 'No description'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {offer.couponStartDate && offer.validityDays
                    ? `Expires on : ${getExpirationDate(offer.couponStartDate, offer.validityDays)}`
                    : 'Expiration date not available'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Code: {offer.couponCode}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 120, textAlign: "right" }}>
                <Typography fontWeight="bold" color="primary">
                  {formatOfferValue(offer)}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleChange(offerId, -1, offer)}
                    disabled={quantity === 0}
                  >
                    <RemoveIcon />
                  </IconButton>
                  <TextField
                    size="small"
                    value={quantity}
                    inputProps={{
                      style: { width: 30, textAlign: "center" },
                      readOnly: true,
                    }}
                    sx={{ mx: 1 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleChange(offerId, 1, offer)}
                    disabled={
                      (offer.couponPerUserLimit !== undefined && offer.couponPerUserLimit !== null && quantity >= offer.couponPerUserLimit) ||
                      (offer.totalCouponCount !== undefined && offer.totalCouponCount !== null && quantity >= offer.totalCouponCount)
                    }
                  >
                    <AddIcon />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          );
        })
      )}
      <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 2 }}>
        <Button variant="contained" onClick={onAddOffer} sx={{ marginRight: '5px' }}>
          Add Offers
        </Button>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button 
          variant="contained" 
          onClick={handleSave} 
          disabled={assignLoading || offers.length === 0}
          sx={{ marginRight: '5px' }}
        >
          {assignLoading ? (
            <>
              <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
              Saving...
            </>
          ) : (
            'Save'
          )}
        </Button>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </Box>
    </Box>
  );
};

export default AssignOffersModalContent; 